import React from 'react';
import { useSelector } from 'react-redux';
import Tooltip from 'housecall-ui/dist/DesignSystem/DataDisplay/Tooltips/Tooltip';
import Typography from 'housecall-ui/dist/DesignSystem/DataDisplay/Typography/Typography';
import IconButton from 'housecall-ui/dist/DesignSystem/Inputs/Buttons/IconButton';
import FormControlLabel from 'housecall-ui/dist/DesignSystem/Inputs/FormControls/FormControlLabel';
import Radio from 'housecall-ui/dist/DesignSystem/Inputs/RadioButtons/Radio';
import Grid from 'housecall-ui/dist/DesignSystem/Layout/Grid/Grid';
import Link from 'housecall-ui/dist/DesignSystem/Navigation/Link/Link';
import makeStyles from 'housecall-ui/dist/DesignSystem/Styles/makeStyles';
import InfoOutlinedIcon from 'housecall-ui/dist/IconsAndImages/Icons/InfoOutlined';
import { HCPTheme } from 'housecall-ui/dist/Theme/Theme';
import { isAdminPanel } from 'utils/isAdminPanel';
import { isBlank } from 'utils/isBlank';

import { REQUIRED_MOST_POPULAR } from 'components/PayWall/Redux/Actions';
import { PayWallDuration, PayWallPlan, PayWallTiers } from 'components/PayWall/Redux/Reducer.type';
import {
  getExistingUsersCount,
  getOriginalCurrentPlanV3,
  getSelectedDuration,
  PAYWALL_PLAN_SEATS,
} from 'components/PayWall/Redux/Selectors';
import { useTrackEventsService } from 'components/Services/Analytics/useTrackEventsService';

import { PAY_WALL_VERSION } from './PayWallDialog';
import PlanHeaderText from './PlanSelectorHeaderText';
import PriceText from './PlanSelectorPriceText';

const useStyles = makeStyles((theme: HCPTheme) => ({
  planOptionContainer: {
    alignItems: 'center',
    margin: theme.spacing(1, 0),
  },
}));

const useMainPriceText = (plan: PayWallPlan) => {
  const originalCurrentPlan = useSelector(getOriginalCurrentPlanV3);
  const selectedDuration = useSelector(getSelectedDuration);

  const monthlyPrice = plan.rate_flat_monthly;
  const sixMonthsPrice = plan.rate_flat_6_months;
  const annualPrice = plan.rate_flat_annual;
  const freeTrial = originalCurrentPlan.tier === PayWallTiers.TRIAL;
  let price = freeTrial ? annualPrice : monthlyPrice;

  if (selectedDuration === PayWallDuration.SixMonths) {
    price = sixMonthsPrice ?? '';
  } else if (selectedDuration === PayWallDuration.Annual) {
    price = annualPrice;
  } else if (selectedDuration === PayWallDuration.Monthly) {
    price = monthlyPrice;
  }

  return `$${price}`;
};

const useDiscountPriceText = (plan: PayWallPlan) => {
  const selectedDuration = useSelector(getSelectedDuration);
  const monthlyPrice = plan.rate_flat_monthly_regular;
  const sixMonthsPrice = plan.rate_flat_6_months_regular;
  const annualPrice = plan.rate_flat_annual_regular;
  let price = monthlyPrice;

  if (selectedDuration === PayWallDuration.SixMonths) {
    price = sixMonthsPrice ?? '';
  } else if (selectedDuration === PayWallDuration.Annual) {
    price = annualPrice;
  } else if (selectedDuration === PayWallDuration.Monthly) {
    price = monthlyPrice;
  }

  if (isBlank(plan.discount_label)) {
    return '';
  }

  return `Reg. $${price}`;
};

const additionalPlanInfo = (tier: string, required: string, isCurrentPlan: boolean) => {
  if (isCurrentPlan) {
    return 'current plan';
  }

  if (tier === required) {
    return 'required';
  }

  if (required === REQUIRED_MOST_POPULAR && tier === PayWallTiers.SMALL) {
    return 'most popular';
  }

  return null;
};

const loginsText = (tier: PayWallPlan) => {
  const freeSeats = tier.number_of_free_seats;

  // We treat 0 on the backend as 'unlimited'
  if (freeSeats === 0) {
    return 'Unlimited logins';
  }

  if (freeSeats <= PAYWALL_PLAN_SEATS.FREE) {
    return '1 login';
  }

  return `1 - ${freeSeats} logins`;
};

const useTooltipStyles = makeStyles((theme: HCPTheme) => ({
  listItemTooltip: {
    fontSize: theme.typography.body2.fontSize,
    backgroundColor: theme.palette.common.white,
    color: 'rgba(0, 0, 0, 0.87)',
    boxShadow: theme.shadows[1],
  },
}));

const DisabledByEmployeesBanner = (props: { availableLogins: number }) => {
  const { availableLogins } = props;
  const classes = useTooltipStyles();
  const existingUsersCount = useSelector(getExistingUsersCount);
  const { trackBannerViewed } = useTrackEventsService();

  trackBannerViewed({ bannerName: 'Manage employees to select this option', payWallVersion: PAY_WALL_VERSION });

  return (
    <Typography variant="body2">
      {isAdminPanel ? 'Manage employees' : <Link href="/pro/account/employees">Manage employees</Link>} to select this
      option
      <Tooltip
        classes={{ tooltip: classes.listItemTooltip }}
        title={
          <Typography>
            You currently have <strong>{existingUsersCount} employee logins.</strong> Basic plans includes{' '}
            <strong>{availableLogins} employee logins.</strong> Reduce your number of logins to move to this plan.
          </Typography>
        }
      >
        <IconButton size="small" edge="end">
          <InfoOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Typography>
  );
};

interface PlanSelectorProps {
  plan: PayWallPlan;
  selectedPlan: PayWallPlan;
  currentPlan: PayWallPlan;
  required: string;
  isDisabledByEmployees?: boolean;
  didSelectPlan: (plan: PayWallPlan) => void;
}

const PlanSelector = (props: PlanSelectorProps) => {
  const { plan, selectedPlan, currentPlan, didSelectPlan, required, isDisabledByEmployees = false } = props;
  const classes = useStyles();
  const isCurrentPlan = currentPlan.id === plan.id;
  const isSelectedPlan = selectedPlan.id === plan.id;

  const extraInfo = additionalPlanInfo(plan.tier, required, isCurrentPlan) ?? '';
  const mainPrice = useMainPriceText(plan);
  const discountPrice = useDiscountPriceText(plan);

  return (
    <Grid container item data-testid="plan-selector">
      <PlanHeaderText planName={plan.display_name ?? ''} extraInfo={extraInfo} />
      <Grid classes={{ root: classes.planOptionContainer }} container spacing={0}>
        <Grid item xs={6}>
          <FormControlLabel
            aria-label="select plan"
            label={loginsText(plan)}
            control={
              <Radio
                name={loginsText(plan)}
                value={plan.id}
                checked={isSelectedPlan}
                color="primary"
                disabled={isDisabledByEmployees}
                onChange={() => didSelectPlan(plan)}
              />
            }
          />
        </Grid>
        <Grid container item justify="flex-end" xs={6} data-testid="plan-selector-price">
          <PriceText price={mainPrice} />
          {discountPrice ? <PriceText price={discountPrice} faded /> : null}
        </Grid>
      </Grid>
      {isDisabledByEmployees && <DisabledByEmployeesBanner availableLogins={plan.number_of_free_seats} />}
    </Grid>
  );
};

export default PlanSelector;
